import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import Pagination from '../../components/Pagination';
import {
  AGENT_STATS_PAGE_SIZE,
  AGENT_STATS_ROLE_OPTIONS,
  buildAgentStatsExportParams,
  buildAgentStatsPageParams,
  defaultUserStatsDateRange,
  formatReportAmount,
  formatReportCount,
  validateUserStatsDateRange,
} from './agencyStatisticsReportConstants';

const DEFAULT_RANGE = defaultUserStatsDateRange();

const EMPTY_FILTERS = {
  roleId: '1',
  startDateInput: DEFAULT_RANGE.startDateInput,
  endDateInput: DEFAULT_RANGE.endDateInput,
  agentId: '',
  agentUsername: '',
};

const TABLE_COLUMNS = [
  '日期',
  '代理 ID',
  '代理帳號',
  '註冊人數',
  '首存人數',
  '首存總金額',
  '二存人數',
  '活躍人數',
  '下單人數',
  '下單總額',
  '盈虧總額',
  '充值人數',
  '充值總額',
];

const EMPTY_TOTAL = {
  subRegisterNum: 0,
  firstDepositNum: 0,
  totalFirstDeposit: 0,
  secondDepositNum: 0,
  subActiveNum: 0,
  subOrderNum: 0,
  totalOrderPrice: 0,
  totalProfit: 0,
  subDepositNum: 0,
  totalDeposit: 0,
};

function renderTotalCell(total, key, formatter = formatReportCount) {
  return formatter(total?.[key]);
}

export default function AgencyStatisticsReportPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(EMPTY_TOTAL);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.agentDailyReportPageQuery(
        buildAgentStatsPageParams(pageNo, nextFilters)
      );
      setRecords(data?.records || []);
      setSummary(data?.totalDto || EMPTY_TOTAL);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total || 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
      setSummary(EMPTY_TOTAL);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    const rangeError = validateUserStatsDateRange(filters.startDateInput, filters.endDateInput);
    if (rangeError) {
      setError(rangeError);
      return;
    }
    loadRecords(1);
  }

  function handleReset() {
    setFilters(EMPTY_FILTERS);
    setError('');
    loadRecords(1, EMPTY_FILTERS);
  }

  async function handleExport(exportCurrentPageOnly) {
    const rangeError = validateUserStatsDateRange(filters.startDateInput, filters.endDateInput);
    if (rangeError) {
      setError(rangeError);
      return;
    }
    setExporting(true);
    setError('');
    try {
      await adminApi.exportAgentDailyReportExcel(
        buildAgentStatsExportParams(filters, exportCurrentPageOnly, page)
      );
    } catch (err) {
      setError(err.message || '匯出失敗');
    } finally {
      setExporting(false);
    }
  }

  function renderRow(row) {
    return (
      <tr key={`${row.datePeriod}-${row.agentId}`}>
        <td>{row.datePeriod || '—'}</td>
        <td>{row.agentId ?? '—'}</td>
        <td>{row.agentUsername || '—'}</td>
        <td>{formatReportCount(row.subRegisterNum)}</td>
        <td>{formatReportCount(row.firstDepositNum)}</td>
        <td>{formatReportAmount(row.totalFirstDeposit)}</td>
        <td>{formatReportCount(row.secondDepositNum)}</td>
        <td>{formatReportCount(row.subActiveNum)}</td>
        <td>{formatReportCount(row.subOrderNum)}</td>
        <td>{formatReportAmount(row.totalOrderPrice)}</td>
        <td>{formatReportAmount(row.totalProfit)}</td>
        <td>{formatReportCount(row.subDepositNum)}</td>
        <td>{formatReportAmount(row.totalDeposit)}</td>
      </tr>
    );
  }

  return (
    <div className="page">
      <div className="section-header">
        <h2>代理統計報表</h2>
      </div>

      <form className="filter-bar" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="agentStatsRoleId">帳號類型</label>
          <select
            id="agentStatsRoleId"
            value={filters.roleId}
            onChange={(e) => updateFilter('roleId', e.target.value)}
          >
            {AGENT_STATS_ROLE_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="agentStatsStartDate">日期起</label>
          <input
            id="agentStatsStartDate"
            type="date"
            value={filters.startDateInput}
            onChange={(e) => updateFilter('startDateInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="agentStatsEndDate">日期迄</label>
          <input
            id="agentStatsEndDate"
            type="date"
            value={filters.endDateInput}
            onChange={(e) => updateFilter('endDateInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="agentStatsAgentId">代理 ID</label>
          <input
            id="agentStatsAgentId"
            type="text"
            placeholder="請輸入代理 ID"
            value={filters.agentId}
            onChange={(e) => updateFilter('agentId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="agentStatsAgentUsername">代理帳號</label>
          <input
            id="agentStatsAgentUsername"
            type="text"
            placeholder="請輸入代理帳號"
            value={filters.agentUsername}
            onChange={(e) => updateFilter('agentUsername', e.target.value)}
          />
        </div>

        <div className="filter-bar__actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset} disabled={loading}>
            重置
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(true)}
          >
            {exporting ? '匯出中…' : '匯出Excel（當前頁面）'}
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(false)}
          >
            {exporting ? '匯出中…' : '匯出Excel（全部資料）'}
          </button>
        </div>
      </form>

      {error ? <p className="form-error">{error}</p> : null}
      {loading ? <p className="page-status">載入中…</p> : null}

      <div className="table-wrap table-wrap--wide">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  暫無資料
                </td>
              </tr>
            ) : (
              records.map((row) => renderRow(row))
            )}
            {!loading && records.length > 0 ? (
              <tr className="data-table__summary-row">
                <td colSpan={3}>匯總</td>
                <td>{renderTotalCell(summary, 'subRegisterNum')}</td>
                <td>{renderTotalCell(summary, 'firstDepositNum')}</td>
                <td>{renderTotalCell(summary, 'totalFirstDeposit', formatReportAmount)}</td>
                <td>{renderTotalCell(summary, 'secondDepositNum')}</td>
                <td>{renderTotalCell(summary, 'subActiveNum')}</td>
                <td>{renderTotalCell(summary, 'subOrderNum')}</td>
                <td>{renderTotalCell(summary, 'totalOrderPrice', formatReportAmount)}</td>
                <td>{renderTotalCell(summary, 'totalProfit', formatReportAmount)}</td>
                <td>{renderTotalCell(summary, 'subDepositNum')}</td>
                <td>{renderTotalCell(summary, 'totalDeposit', formatReportAmount)}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={AGENT_STATS_PAGE_SIZE}
        onPageChange={(nextPage) => loadRecords(nextPage)}
      />
    </div>
  );
}
